import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import MainLayout from '../components/MainLayout';
import { api } from '../api/api';
import usePageTitle from '../utils/usePageTitle';
import { useToast } from '../components/ToastContainer';
import { useGlobalLoading } from '../hooks/useGlobalLoading';

// Тип для ответа API ClothesDetails - данные приходят напрямую
interface ClothesDetailsResponse {
  id: number;
  size: string | { id: number; size: string };
  brand: string | { brand: string };
  model?: string;
  color?: string;
  price: number;
  article: string;
  condition: string | { condition: string };
  city: string;
  place: string;
  fitting: boolean;
  vendor?: {
    first_name: string;
    last_name: string;
    vk_account: string;
    telegram_account: string;
    show_vk: boolean | null;
    show_telegram: boolean | null;
    show_first_name: boolean | null;
    show_last_name: boolean | null;
    show_username: boolean | null;
  };
  photo_url: string;
  created_at?: string;
  updated_at?: string;
}

// Стилизованные компоненты
const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #609F94;
  margin-bottom: 24px;
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 16px;
  padding: 0;
`;

const Content = styled.div`
  display: flex;
  gap: 32px;

  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

const ImageContainer = styled.div`
  width: 350px;
  height: 350px;
  border-radius: 16px;
  overflow: hidden;
  flex-shrink: 0;

  @media (max-width: 1024px) {
    width: 100%;
    height: auto;
    aspect-ratio: 1/1;
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  background-color: transparent;
`;

const DetailsContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Title = styled.h1`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 700;
  font-size: 28px;
  color: #FFFFFF;
  margin: 0;
`;

const Subtitle = styled.h2`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 18px;
  color: #609F94;
  margin: 0;
`;

const PriceInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PriceSection = styled.div`
  display: flex;
  align-items: baseline;
  gap: 16px;
`;

const Price = styled.span`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 700;
  font-size: 24px;
  color: #FFFFFF;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 24px;
  margin-top: 16px;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DetailLabel = styled.span`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #609F94;
`;

const DetailValue = styled.span`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 16px;
  color: #FFFFFF;
`;

const VendorSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  background-color: #172A2A;
  border-radius: 16px;
  margin-top: 24px;
  grid-column: 1 / -1;
`;

const VendorTitle = styled.h3`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 16px;
  color: #FFFFFF;
  margin: 0;
`;

const VendorInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const VendorRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const VendorContact = styled.a`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #24BA7D;
  text-decoration: none;
`;

const BackArrowIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15.8332 10H4.1665" stroke="#609F94" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9.99984 15.8334L4.1665 10L9.99984 4.16669" stroke="#609F94" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ClothesDetails: React.FC = () => {
  usePageTitle('Детали одежды');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showError } = useToast();
  const { addLoadingStep, removeLoadingStep } = useGlobalLoading();

  const [clothesDetails, setClothesDetails] = useState<ClothesDetailsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchClothesDetails();
    }
  }, [id]);

  const fetchClothesDetails = async () => {
    if (!id) return;

    try {
      addLoadingStep('clothes-details', 'Загружаем детали одежды...', 5);
      setLoading(true);
      
      const response = await api.getClothesById(parseInt(id));
      // API фактически возвращает данные напрямую, а не в обертке
      setClothesDetails(response as unknown as ClothesDetailsResponse);
    } catch (error) {
      console.error('Ошибка при загрузке деталей одежды:', error);
      showError('Ошибка при загрузке деталей одежды');
      navigate('/inventory');
    } finally {
      setLoading(false);
      removeLoadingStep('clothes-details');
    }
  };

  const handleGoBack = () => {
    navigate('/inventory');
  };

  // Функции для безопасного извлечения значений
  const getBrandName = (brand: string | { brand: string }): string => {
    return typeof brand === 'string' ? brand : brand?.brand || 'Без бренда';
  };

  const getSizeName = (size: string | { id: number; size: string }): string => {
    return typeof size === 'string' ? size : size?.size || 'Без размера';
  };

  const getConditionName = (condition: string | { condition: string }): string => {
    return typeof condition === 'string' ? condition : condition?.condition || 'Без состояния';
  };

  if (loading || !clothesDetails) {
    return (
      <MainLayout>
        <Container>
          <div>Загрузка...</div>
        </Container>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Container>
        <BackButton onClick={handleGoBack}>
          <BackArrowIcon />
          Назад
        </BackButton>

        <Content>
          <ImageContainer>
            <Image 
              src={clothesDetails.photo_url || 'https://placehold.co/350x350'} 
              alt={`${getBrandName(clothesDetails.brand)} ${clothesDetails.model || clothesDetails.article}`}
            />
          </ImageContainer>

          <DetailsContainer>
            <TitleSection>
              <Title>{getBrandName(clothesDetails.brand)}</Title>
              <Subtitle>{clothesDetails.model || clothesDetails.article}</Subtitle>
            </TitleSection>

            <PriceInfoContainer>
              <PriceSection>
                <Price>{clothesDetails.price} ₽</Price>
              </PriceSection>
            </PriceInfoContainer>

            <DetailGrid>
              <DetailItem>
                <DetailLabel>Артикул</DetailLabel>
                <DetailValue>{clothesDetails.article}</DetailValue>
              </DetailItem>

              <DetailItem>
                <DetailLabel>Размер</DetailLabel>
                <DetailValue>{getSizeName(clothesDetails.size)}</DetailValue>
              </DetailItem>

              <DetailItem>
                <DetailLabel>Состояние</DetailLabel>
                <DetailValue>{getConditionName(clothesDetails.condition)}</DetailValue>
              </DetailItem>

              {clothesDetails.color && (
                <DetailItem>
                  <DetailLabel>Цвет</DetailLabel>
                  <DetailValue>{clothesDetails.color}</DetailValue>
                </DetailItem>
              )}

              <DetailItem>
                <DetailLabel>Местоположение</DetailLabel>
                <DetailValue>
                  {clothesDetails.city}
                  {clothesDetails.place && `, ${clothesDetails.place}`}
                </DetailValue>
              </DetailItem>

              <DetailItem>
                <DetailLabel>Примерка</DetailLabel>
                <DetailValue>{clothesDetails.fitting ? 'Доступна' : 'Не доступна'}</DetailValue>
              </DetailItem>

              {clothesDetails.vendor && (
                <VendorSection>
                  <VendorTitle>Продавец</VendorTitle>
                  <VendorInfo>
                    {(clothesDetails.vendor.show_first_name !== false && clothesDetails.vendor.first_name) && (
                      <DetailValue>
                        {clothesDetails.vendor.first_name} {clothesDetails.vendor.last_name}
                      </DetailValue>
                    )}
                    
                    {(clothesDetails.vendor.show_telegram !== false && clothesDetails.vendor.telegram_account) && (
                      <VendorRow>
                        <DetailLabel>Telegram:</DetailLabel>
                        <VendorContact 
                          href={`https://t.me/${clothesDetails.vendor.telegram_account.replace('@', '')}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          {clothesDetails.vendor.telegram_account}
                        </VendorContact>
                      </VendorRow>
                    )}
                    
                    {(clothesDetails.vendor.show_vk !== false && clothesDetails.vendor.vk_account) && (
                      <VendorRow>
                        <DetailLabel>VK:</DetailLabel>
                        <VendorContact 
                          href={clothesDetails.vendor.vk_account} 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          {clothesDetails.vendor.vk_account}
                        </VendorContact>
                      </VendorRow>
                    )}
                  </VendorInfo>
                </VendorSection>
              )}
            </DetailGrid>
          </DetailsContainer>
        </Content>
      </Container>
    </MainLayout>
  );
};

export default ClothesDetails;
